import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EvenementsService, Evenement } from '../../services/evenements.service';
import { FaPlus, FaTrash, FaEdit, FaCalendar, FaEye, FaHeart, FaFilePdf, FaStar, FaRegStar } from 'react-icons/fa';
import { format } from 'date-fns';
import { getImageUrl } from '../../services/api';

const EvenementListPage: React.FC = () => {
    const [evenements, setEvenements] = useState<Evenement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadEvenements();
    }, []);

    const loadEvenements = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await EvenementsService.findAll(1, 100);

            // Handle both array and paginated responses
            if (Array.isArray(response)) {
                setEvenements(response);
            } else {
                setEvenements(response.data);
            }
        } catch (err) {
            setError('Erreur lors du chargement des événements');
            console.error('Failed to load evenements', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string, titre: string) => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'événement "${titre}" ?`)) {
            try {
                await EvenementsService.delete(id);
                loadEvenements();
            } catch (err) {
                alert('Erreur lors de la suppression de l\'événement');
                console.error('Failed to delete evenement', err);
            }
        }
    };

    const handleTogglePublish = async (id: string, currentState: boolean) => {
        try {
            await EvenementsService.update(id, { isPublishedOnHome: !currentState });
            loadEvenements();
        } catch (err) {
            alert('Erreur lors de la modification de l\'état de publication');
            console.error('Failed to toggle publish evenement', err);
        }
    };

    const formatDateRange = (dateDebut: string, dateFin: string) => {
        const debut = new Date(dateDebut);
        const fin = new Date(dateFin);

        if (debut.getTime() === fin.getTime()) {
            return format(debut, 'd MMMM yyyy');
        }

        return `${format(debut, 'd MMM')} - ${format(fin, 'd MMM yyyy')}`;
    };

    const getEventStatus = (dateDebut: string, dateFin: string) => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const debut = new Date(dateDebut);
        debut.setHours(0, 0, 0, 0);
        const fin = new Date(dateFin);
        fin.setHours(0, 0, 0, 0);

        if (debut > now) return { label: 'À venir', color: 'bg-blue-100 text-blue-800' };
        if (fin < now) return { label: 'Passé', color: 'bg-gray-100 text-gray-800' };
        return { label: 'En cours', color: 'bg-green-100 text-green-800' };
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Gestion des Événements</h1>
                    <p className="text-gray-600 mt-2">Créer, modifier et gérer tous les événements</p>
                </div>
                <Link
                    to="/admin/evenements/create"
                    className="bg-primary text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors shadow-md"
                >
                    <FaPlus /> Nouvel Événement
                </Link>
            </div>

            {loading ? (
                <div className="text-center py-16">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    <p className="mt-4 text-gray-600">Chargement des événements...</p>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <p className="text-red-800">{error}</p>
                    <button
                        onClick={loadEvenements}
                        className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            ) : evenements.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <FaCalendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun événement</h3>
                    <p className="text-gray-600 mb-6">Commencez par créer votre premier événement</p>
                    <Link
                        to="/admin/evenements/create"
                        className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
                    >
                        <FaPlus /> Créer un événement
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {evenements.map((evenement) => {
                        const status = getEventStatus(evenement.dateDebut, evenement.dateFin);

                        return (
                            <div
                                key={evenement.id}
                                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-100"
                            >
                                {/* Image Section */}
                                <div className="h-48 bg-gradient-to-br from-primary/10 to-secondary/10 relative overflow-hidden">
                                    {evenement.image ? (
                                        <img
                                            src={getImageUrl(evenement.image)}
                                            alt={evenement.titre}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <FaCalendar className="h-16 w-16 text-primary/30" />
                                        </div>
                                    )}
                                    <div className="absolute top-3 right-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                                            {status.label}
                                        </span>
                                    </div>
                                </div>

                                {/* Content Section */}
                                <div className="p-5">
                                    <h3 className="font-bold text-xl mb-3 text-gray-900 line-clamp-2">
                                        {evenement.titre}
                                    </h3>

                                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                                        <FaCalendar className="text-primary" />
                                        <span>{formatDateRange(evenement.dateDebut, evenement.dateFin)}</span>
                                    </div>

                                    {evenement.description && (
                                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                            {evenement.description}
                                        </p>
                                    )}

                                    {/* Engagement Stats */}
                                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <FaEye className="text-blue-500" />
                                            <span>{evenement.nombreVues}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <FaHeart className="text-red-500" />
                                            <span>{evenement.nombreLikes}</span>
                                        </div>
                                        {evenement.pdfProgramme && (
                                            <div className="flex items-center gap-1">
                                                <FaFilePdf className="text-red-600" />
                                                <span>PDF</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => handleTogglePublish(evenement.id, evenement.isPublishedOnHome)}
                                            className={`flex-1 px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium border ${
                                                evenement.isPublishedOnHome 
                                                ? 'bg-warning-bg border-highlight-light text-highlight-dark hover:bg-highlight hover:text-white hover:border-highlight shadow-sm' 
                                                : 'bg-white border-gray-30 text-gray-60 hover:text-primary hover:bg-primary-50 hover:border-primary shadow-sm'
                                            }`}
                                        >
                                            {evenement.isPublishedOnHome ? <FaStar className="text-lg" /> : <FaRegStar className="text-lg" />}
                                            <span className="hidden lg:inline">
                                                {evenement.isPublishedOnHome ? "Retirer de l'accueil" : "Publier sur l'accueil"}
                                            </span>
                                            <span className="lg:hidden inline">
                                                {evenement.isPublishedOnHome ? "Retirer" : "Publier"}
                                            </span>
                                        </button>
                                        <Link
                                            to={`/admin/evenements/${evenement.id}/edit`}
                                            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center text-sm font-medium"
                                            title="Modifier"
                                        >
                                            <FaEdit />
                                        </Link>
                                        <button
                                            onClick={() => handleDelete(evenement.id, evenement.titre)}
                                            className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                            title="Supprimer"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default EvenementListPage;
